import {
  cloneElement,
  type ComponentPropsWithRef,
  type ElementType,
  type FC,
  isValidElement,
  type JSX,
  type ReactElement,
  type ReactNode,
} from 'react';

import { pick } from 'lodash-es';

import Skeleton from '@mui/material/Skeleton';

import { isPrimitive } from '@craft/lib/primitives';

import type * as types from './types';

import { type QuantitativeSize } from '../../types/sizes';
import { nodeHasRenderableContent } from '../../util/elements';
import { mergeSx } from '../../util/mui/sx';
import { quantitativeSizeToString } from '../../util/sizes';
import { Actions } from '../actions/Actions';
import { type Action } from '../actions/types';
import { type ImageComponent } from '../images/Image';
import {
  PolymorphicStack,
  type PolymorphicStackProps,
  type StackDefaultComponent,
} from '../structural/PolymorphicStack';
import { Stack, type StackProps } from '../structural/Stack';
import { Typography, type TypographyProps } from '../typography/Typography';
import { IfNodeRenders } from '../util/IfNodeRenders';

import { UserAvatar, type UserAvatarProps } from './UserAvatar';
import { UserNameText } from './UserNameText';

export type UserTilePartialProps = {
  readonly actions?: Action[];
  /**
   * A custom node rendered in place of the default {@link UserAvatar}. The user's avatar data
   * belongs on the `avatar` prop (from {@link types.UserInfo}); this prop replaces the rendered
   * element entirely.
   */
  readonly avatarNode?: ReactNode;
  readonly avatarProps?: Omit<UserAvatarProps, 'alt' | 'avatar' | 'component' | 'email' | 'size'>;
  readonly avatarSize?: UserAvatarProps['size'];
  /**
   * A render prop that can be used to customize the layout of the content area that sits to the
   * right of the {@link UserAvatar} in the {@link UserTile} component.  The element returned from
   * this render prop must accept props of type {@link RightContentLayoutProps} to ensure that it
   * can be used in the context of the {@link UserTile} component.
   *
   * If this prop is not provided, a default layout will be applied to the content area.
   *
   * @see {@link UserTileRightContentRenderProp}
   */
  readonly children?: UserTileRightContentRenderProp;
  readonly height?: 'auto' | QuantitativeSize<'px'>;
  readonly imageComponent: ImageComponent;
  readonly isLoading?: boolean;
  readonly subTitle?: ReactNode;
  readonly subTitleProps?: Omit<TypographyProps, 'children' | 'title'>;
  readonly title?: ReactNode;
  readonly titleProps?: Omit<TypographyProps, 'children' | 'title'>;
} & Partial<types.UserInfo>;

/**
 * Defines the props that must be accepted by the element that is returned from the render prop,
 * {@link UserTileRightContentRenderProp}, that is used to customize the layout of the content area
 * that sits to the right of the {@link UserAvatar} in the {@link UserTile} component.
 */
type RightContentLayoutProps = Pick<StackProps, 'sx'>;

/**
 * Defines a render prop that can be used to customize the layout of the content area that sits to
 * the right of the {@link UserAvatar} in the {@link UserTile} component.  The element returned from
 * this render prop must accept props of type {@link RightContentLayoutProps} to ensure that it can
 * be used in the context of the {@link UserTile} component.
 */
type UserTileRightContentRenderProp = (props: {
  readonly children: ReactNode;
}) => ReactElement<{ sx?: StackProps['sx'] }>;

const RequiredLayout = {
  flexDirection: 'column',
  flexGrow: 100,
  justifyContent: 'center',
  minWidth: 0,
} as const;

/**
 * Ensures that the element, {@link ReactElement}, that is returned from the render prop,
 * {@link UserTileRightContentRenderProp}, contains the necessary style information defined in
 * {@link RequiredLayout} to work in the context of the {@link UserTile} component.
 */
const enforceLayout = (node: ReactNode): ReactNode =>
  isValidElement<RightContentLayoutProps>(node)
    ? cloneElement(node, { sx: mergeSx(node.props.sx, RequiredLayout) })
    : node;

const RenderPropWrapper = ({
  children,
  renderer,
}: {
  readonly children: ReactNode;
  readonly renderer?: UserTileRightContentRenderProp;
}) => {
  if (renderer !== undefined) {
    const content = renderer({ children });
    return enforceLayout(content);
  }
  return <Stack sx={RequiredLayout}>{children}</Stack>;
};

const TileTextRow: FC<Omit<StackProps, 'alignItems' | 'direction'>> = ({ children, ...props }) => (
  <Stack {...props} alignItems='center' direction='row'>
    {children}
  </Stack>
);

const TileTitleHeight = 22;

const TitlePropNames = [
  'firstName',
  'fullName',
  'isLoading',
  'lastName',
  'title',
  'titleProps',
] as const;

interface UserTileTitleProps extends Pick<UserTilePartialProps, (typeof TitlePropNames)[number]> {}

const UserTileTitle: FC<UserTileTitleProps> = props => (
  <TileTextRow>
    {props.isLoading ? (
      <Skeleton
        data-testid='user-tile-name-skeleton'
        height={quantitativeSizeToString(TileTitleHeight, 'px')}
        variant='text'
        width='60%'
      />
    ) : isPrimitive(props.title, { exclude: ['null', 'undefined'] }) ? (
      <UserNameText
        lineHeight={quantitativeSizeToString(TileTitleHeight, 'px')}
        truncate
        wordBreak='break-all'
        {...props.titleProps}
      >
        {props.title}
      </UserNameText>
    ) : nodeHasRenderableContent(props.title) ? (
      props.title
    ) : (
      <UserNameText
        lineHeight={quantitativeSizeToString(TileTitleHeight, 'px')}
        truncate
        wordBreak='break-all'
        {...props.titleProps}
        firstName={props.firstName}
        fullName={props.fullName}
        lastName={props.lastName}
      />
    )}
  </TileTextRow>
);

const TileSubTitleHeight = 20;

const SubTitlePropNames = ['isLoading', 'subTitle', 'subTitleProps'] as const;

interface UserTileSubTitleProps extends Pick<
  UserTilePartialProps,
  (typeof SubTitlePropNames)[number]
> {}

const UserTileSubTitle: FC<UserTileSubTitleProps> = props => (
  <TileTextRow>
    {props.isLoading ? (
      <Skeleton
        data-testid='user-tile-sub-title-skeleton'
        height={quantitativeSizeToString(TileSubTitleHeight, 'px')}
        variant='text'
        width='40%'
      />
    ) : isPrimitive(props.subTitle, { exclude: ['null', 'undefined'] }) ? (
      <Typography
        color='text.secondary'
        lineClamp={1}
        lineHeight={quantitativeSizeToString(TileSubTitleHeight, 'px')}
        truncate
        variant='body2'
        wordBreak='break-all'
        {...props.subTitleProps}
        title={props.subTitle.toString()}
      >
        {props.subTitle}
      </Typography>
    ) : (
      props.subTitle
    )}
  </TileTextRow>
);

interface UserTileRightContentProps extends Pick<
  UserTilePartialProps,
  'children' | (typeof SubTitlePropNames)[number] | (typeof TitlePropNames)[number]
> {}

/**
 * Defines the content of {@link UserTile} that sits to the right of the {@link UserAvatar} and
 * includes the title (usually the user's name) and sub title (usually the user's role and/or
 * organization).
 *
 * The component is designed so that the outer wrapping element can be provided as a render prop
 * to customize the layout of this content area while the result of this render prop is modified
 * to ensure that it contains the necessary style information to work in the context of the
 * {@link UserTile} component.
 *
 * If no render prop is provided, a default layout will be applied.
 */
const UserTileRightContent: FC<UserTileRightContentProps> = props => (
  <RenderPropWrapper renderer={props.children}>
    <UserTileTitle {...pick(props, TitlePropNames)} />
    <IfNodeRenders node={props.subTitle}>
      <UserTileSubTitle {...pick(props, SubTitlePropNames)} />
    </IfNodeRenders>
  </RenderPropWrapper>
);

export type UserTileProps<
  E extends ElementType = StackDefaultComponent,
  P = UserTilePartialProps,
> = P &
  PolymorphicStackProps<
    E,
    'children' | 'direction' | 'flexShrink' | 'overflow' | 'position' | 'title'
  >;

export const UserTile = <E extends ElementType = StackDefaultComponent>({
  actions,
  avatar,
  avatarNode,
  avatarProps,
  height = '40px',
  avatarSize = { height: '100%' },
  children,
  email,
  firstName,
  fullName,
  imageComponent,
  initials,
  isLoading,
  lastName,
  ref,
  subTitle,
  subTitleProps,
  title,
  titleProps,
  ...props
}: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & UserTileProps<E>): JSX.Element => (
  <PolymorphicStack
    alignItems='center'
    gap={1}
    maxWidth='100%'
    width='100%'
    {...(props as PolymorphicStackProps<E>)}
    direction='row'
    flexShrink={0}
    overflow='hidden'
    position='relative'
    ref={ref}
    sx={mergeSx(props.sx, { height })}
  >
    {avatarNode ?? (
      <UserAvatar
        color='text.primary'
        {...avatarProps}
        alt='user-avatar'
        avatar={avatar}
        component={imageComponent}
        email={email}
        firstName={firstName}
        fullName={fullName}
        initials={initials}
        lastName={lastName}
        size={avatarSize}
      />
    )}
    <UserTileRightContent
      firstName={firstName}
      fullName={fullName}
      isLoading={isLoading}
      lastName={lastName}
      subTitle={subTitle}
      subTitleProps={subTitleProps}
      title={title}
      titleProps={titleProps}
    >
      {children}
    </UserTileRightContent>
    <Actions actions={actions} />
  </PolymorphicStack>
);
